import * as Sentry from '@sentry/browser'
import { QueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { FirebaseOptions } from 'firebase/app'
import { appConfig } from '~/constants/appConfig'
import { NewsRequest } from '~/types/request/news'
import { NoctuaGoldSkuRequest } from '~/types/request/shop'

const isDev = import.meta.env.VITE_APP_ENV === 'development'

// Sentry Instance
export const sentryInstance: Sentry.BrowserOptions = {
  dsn: appConfig.sentry.dsn,
  environment: appConfig.environment,

  // Optional configuration for better error reporting
  tracesSampleRate: 1.0, // Set to 1.0 to capture 100% of transactions
}

type TrackerEnvironment = 'sandbox' | 'production'

// Adjust Instance
export const adjustInstance: { appToken: string; environment: TrackerEnvironment } = {
  appToken: appConfig.tracker.adjust.appToken,
  environment: appConfig.tracker.adjust.environment,
}

// Firebase Instance
export const firebaseInstance: { options: FirebaseOptions } = {
  options: {
    apiKey: appConfig.tracker.firebase.apiKey,
    authDomain: appConfig.tracker.firebase.authDomain,
    projectId: appConfig.tracker.firebase.projectId,
    storageBucket: appConfig.tracker.firebase.storageBucket,
    messagingSenderId: appConfig.tracker.firebase.messagingSenderId,
    appId: appConfig.tracker.firebase.appId,
    measurementId: appConfig.tracker.firebase.measurementId,
  },
}

// Tanstack Query Instance
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: isDev ? 0 : 1000 * 10, // 10s in prod
      refetchOnWindowFocus: isDev,
      refetchOnReconnect: isDev,
      retry: !isDev,
    },
  },
})

export const queryKeys = {
  webview: {
    tempLoginKey: () => ['webview', 'temp-login-key'],
    url: () => ['webview', 'url'],
    autoLoginUrl: () => ['webview', 'auto-login-url'],
  },
  auth: {
    me: () => ['auth', 'me'],
    all: () => ['auth'],
    login: () => ['auth', 'login'],
  },
  games: {
    all: () => ['games'],
    allNative: () => ['native-games'],
    allInstant: () => ['instant-games'],
  },
  gold: {
    noctuaGold: () => ['gold'],
    noctuaBoundGold: () => ['boundGold'],
  },
  shop: {
    noctuaGoldSkus: (params: NoctuaGoldSkuRequest) => [
      'noctua-gold-skus',
      params.currency,
      params.countryCode,
    ],
  },
  news: {
    all: (params: NewsRequest) => ['news', params.pageIndex, params.pageSize, params.game_code],
    detail: (id: number) => ['news', id],
  },
  reward: {
    gachaTicketAmount: () => ['gacha-ticket-amount'],
    gachaHistory: () => ['gacha-history'],
    allRewards: () => ['all-gacha-rewards'],
  },
}

// Axios Instance
export const createAxiosInstance = () => {
  return axios.create({
    baseURL: appConfig.apiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 10000, // 10 seconds timeout
  })
}

export const createSdkAxiosInstance = () => {
  const bundleId = appConfig.bundleId
  const osVersion = navigator.userAgent
  const osAgent = navigator.userAgent
  const sdkVersion = appConfig.version
  return axios.create({
    baseURL: appConfig.sdkApiUrl,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': 'en',
      'X-CLIENT-ID': appConfig.clientId,
      'X-BUNDLE-ID': bundleId,
      'X-PLATFORM': 'direct',
      'X-OS-VERSION': osVersion,
      'X-OS-AGENT': osAgent,
      'X-SDK-VERSION': sdkVersion,
    },
    timeout: 10000, // 10 seconds timeout
  })
}

export const createPlatformAxiosInstance = () => {
  return axios.create({
    baseURL: appConfig.platformApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 10000, // 10 seconds timeout
  })
}

// Create axios instance
export const axiosInstance = createAxiosInstance()
axiosInstance.interceptors.response.use(
  (response) => {
    // UNCOMMENT THESE IF YOU WANT TO SEE EVERY SUCCESS RESPONSE LOG
    console.log('✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅')
    console.log(`🚀 [${response.request._method}] ${response.request._url}`)
    // console.log('🚀 Request Headers:', JSON.stringify(response.config.headers, null, 2))
    // console.log('🚀 Request Body:', response.config.data)
    // console.log('📥 Response Data:', JSON.stringify(response.data, null, 2).substring(0, 100))
    // END

    return response
  },
  (error) => {
    // UNCOMMENT THESE IF YOU WANT TO SEE EVERY ERROR RESPONSE LOG
    console.log('❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌')
    console.log(`🚀 [${error.request._method}] ${error.request._url}`)
    console.log('🚀 Request Headers:', JSON.stringify(error.config.headers, null, 2))
    // console.log('🚀 Request Body:', error.config.data)
    console.log('📤 Response Data:', JSON.stringify(error.response?.data || error, null, 2))
    // END

    // Handle 401 error
    if (error.response.status === 401) {
      // Clear auth state
    }

    throw error
  },
)

export const sdkAxiosInstance = createSdkAxiosInstance()
sdkAxiosInstance.interceptors.response.use(
  (response) => {
    // UNCOMMENT THESE IF YOU WANT TO SEE EVERY SUCCESS RESPONSE LOG
    console.log('✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅')
    console.log(`🚀 [${response.request._method}] ${response.request._url}`)
    console.log('🚀 Request Headers:', JSON.stringify(response.config.headers, null, 2))
    console.log('🚀 Request Body:', response.config.data)
    console.log('📥 Response Data:', JSON.stringify(response.data, null, 2).substring(0, 100))
    // END

    return response
  },
  (error) => {
    // UNCOMMENT THESE IF YOU WANT TO SEE EVERY ERROR RESPONSE LOG
    console.log('❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌')
    console.log(`🚀 [${error.request._method}] ${error.request._url}`)
    console.log('🚀 Request Headers:', JSON.stringify(error.config.headers, null, 2))
    console.log('🚀 Request Body:', error.config.data)
    console.log('📤 Response Data:', JSON.stringify(error.response?.data || error, null, 2))
    // END

    // Handle 401 error
    if (error.response.status === 401) {
      // Clear auth state
    }

    // return JSON.stringify(error.response?.data || error, null, 2)

    throw error
  },
)

export const platformAxiosInstance = createPlatformAxiosInstance()
platformAxiosInstance.interceptors.response.use(
  (response) => {
    // UNCOMMENT THESE IF YOU WANT TO SEE EVERY SUCCESS RESPONSE LOG
    console.log('✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅✅')
    console.log(`🚀 [${response.request._method}] ${response.request._url}`)
    console.log('🚀 Request Headers:', JSON.stringify(response.config.headers, null, 2))
    console.log('🚀 Request Body:', response.config.data)
    console.log('📥 Response Data:', JSON.stringify(response.data, null, 2).substring(0, 100))
    // END

    return response
  },
  (error) => {
    // UNCOMMENT THESE IF YOU WANT TO SEE EVERY ERROR RESPONSE LOG
    console.log('❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌❌')
    console.log(`🚀 [${error.request._method}] ${error.request._url}`)
    console.log('🚀 Request Headers:', JSON.stringify(error.config.headers, null, 2))
    console.log('🚀 Request Body:', error.config.data)
    console.log('📤 Response Data:', JSON.stringify(error.response?.data || error, null, 2))
    // END

    // Handle 401 error
    if (error.response.status === 401) {
      // Clear auth state
    }

    // return JSON.stringify(error.response?.data || error, null, 2)

    throw error
  },
)
